import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';

import { LocatorLibrary } from 'src/app/locator/models/locator-library';
import { ProfilerLibrary } from 'src/app/profiler/model/profiler-library';
import { formValueExistInArrayValidator } from 'src/app/shared/utils/form-validators/form-value-exist-in-array.validator';

@Component({
  selector: 'atlas-add-edit-library-form',
  templateUrl: './add-edit-library-form.component.html',
  styleUrls: ['./add-edit-library-form.component.less'],
  standalone: false
})
export class AddEditLibraryFormComponent implements OnInit {
  private _library: LocatorLibrary | null = null;
  private _profilerLibraries: ProfilerLibrary[] = [];

  @Input() enableProfilerLibrarySelection = false;

  @Input()
  set profilerLibraries(newValue: ProfilerLibrary[] | null) {
    this._profilerLibraries = newValue ?? [];
    this.filteredProfilerLibraries = [...this._profilerLibraries];

    if (this.addEditLibraryForm) {
      const profilerLibraryIdControl =
        this.addEditLibraryForm.controls.profilerLibraryId;

      profilerLibraryIdControl.setValue(profilerLibraryIdControl.value, {
        emitEvent: false
      });
    }
  }

  get profilerLibraries(): ProfilerLibrary[] {
    return this._profilerLibraries;
  }

  filteredProfilerLibraries: ProfilerLibrary[] = [];

  @Input()
  set library(newValue: LocatorLibrary | null) {
    this._library = newValue;
  }

  get library(): LocatorLibrary | null {
    return this._library;
  }

  addEditLibraryForm: UntypedFormGroup;

  constructor(private fb: UntypedFormBuilder) {}

  private _userLibraries: string[] = [];

  @Input()
  set userLibraries(newValue: string[]) {
    this._userLibraries = [...newValue];

    const libraryNameControl = this.addEditLibraryForm?.controls.libraryName;

    if (!libraryNameControl) {
      return;
    }

    libraryNameControl.setValidators([
      Validators.required,
      formValueExistInArrayValidator(
        this.library
          ? this._userLibraries.filter((item) => item !== this.library?.name)
          : this._userLibraries
      )
    ]);
    libraryNameControl.updateValueAndValidity({ emitEvent: false });
  }

  get userLibraries(): string[] {
    return this._userLibraries;
  }

  ngOnInit() {
    const initialValue: LocatorLibrary = this.library || ({} as LocatorLibrary);

    this.addEditLibraryForm = this.createLibraryForm(initialValue);
  }

  createLibraryForm(initialLibraryValue: LocatorLibrary) {
    return this.fb.group({
      id: initialLibraryValue.id,
      libraryName: [
        initialLibraryValue.name ?? '',
        [
          Validators.required,
          formValueExistInArrayValidator(
            this.library
              ? this._userLibraries.filter(
                  (item) => item !== this.library?.name
                )
              : this._userLibraries
          )
        ]
      ],
      profilerLibraryId: [initialLibraryValue.profilerLibraryId ?? null]
    });
  }

  displayProfilerLibrary = (profilerLibraryId: number | string | null) => {
    if (profilerLibraryId == null) {
      return '';
    }

    if (typeof profilerLibraryId === 'string') {
      return profilerLibraryId;
    }

    return (
      this.profilerLibraries.find(
        (profilerLibrary) => profilerLibrary.id === profilerLibraryId
      )?.name ?? ''
    );
  };

  onProfilerFilterChanged(event: Event) {
    const search = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    if (!search) {
      this.filteredProfilerLibraries = [...this.profilerLibraries];
      return;
    }

    this.filteredProfilerLibraries = this.profilerLibraries.filter(
      (profilerLibrary) => profilerLibrary.name?.toLowerCase().includes(search)
    );
  }

  clearProfilerLibrary(event: MouseEvent) {
    event.stopPropagation();
    this.addEditLibraryForm.controls.profilerLibraryId.setValue(null);
    this.filteredProfilerLibraries = [...this.profilerLibraries];
  }

  getSelectedProfilerLibraryId(): number | null {
    const selectedValue =
      this.addEditLibraryForm.controls.profilerLibraryId.value;

    if (typeof selectedValue === 'number') {
      return selectedValue;
    }

    const matchedProfilerLibrary = this.profilerLibraries.find(
      (profilerLibrary) =>
        profilerLibrary.name?.toLowerCase() ===
        selectedValue?.toString().trim().toLowerCase()
    );

    return matchedProfilerLibrary?.id ?? null;
  }

  emptyLibraryName() {
    if (!this.addEditLibraryForm?.controls) {
      return false;
    }

    if (this.addEditLibraryForm.controls.libraryName.value) {
      return (
        this.addEditLibraryForm.controls.libraryName.value?.trim().length === 0
      );
    }

    return this.addEditLibraryForm.controls.libraryName.errors?.required;
  }

  uniqueLibraryName() {
    if (!this.addEditLibraryForm?.controls) {
      return true;
    }

    return !this.addEditLibraryForm.controls.libraryName.errors
      ?.valueAlreadyExist;
  }

  invalidForm(): boolean {
    return (
      !(this.addEditLibraryForm && this.addEditLibraryForm.controls) ||
      !this.addEditLibraryForm.dirty ||
      !this.addEditLibraryForm.controls?.libraryName.valid ||
      this.emptyLibraryName() ||
      !this.uniqueLibraryName()
    );
  }
}
